import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/Auth/Login';
import SellerRegistrationForm from './components/Auth/SellerRegistrationForm';
import BuyerRegistrationForm from './components/Auth/BuyerRegistrationForm';
import AdminDashboard from './components/Admin/AdminDashboard';
import AccountPage from './components/Auth/AccountPage';
import SellerDashboard from './pages/SellerDashboard';
import BuyerListings from './pages/BuyerListings';
import UserProfile from './pages/UserProfile';
import { Messaging } from './components/Messaging/Messaging';

const RoleEmojis = {
  'Buyer': '🛍️',
  'Seller': '📦',
  'Admin': '⚙️'
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(localStorage.getItem('role') || '');
  const userId = localStorage.getItem('userId');

  const handleLoginSuccess = (response) => {
    console.log('✅ [APP] handleLoginSuccess called with:', response);
    console.log('✅ [APP] Setting isLoggedIn to true');
    setIsLoggedIn(true);
    const roleToSet = response.role || localStorage.getItem('role') || 'Buyer';
    console.log('✅ [APP] Setting role:', roleToSet);
    setUserRole(roleToSet);
    console.log('✅ [APP] State updated, user should be logged in');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('phoneNumber');
    setIsLoggedIn(false);
    setUserRole('');
  };

  // Protected Route Component
  const ProtectedRoute = ({ children }) => {
    return isLoggedIn ? children : <Navigate to="/login" replace />;
  };

  // Dashboard Component
  const Dashboard = () => {
    const effectiveRole = userRole || localStorage.getItem('role') || '';
    const navigate = useNavigate();
    
    if (!isLoggedIn) {
      return <Navigate to="/login" replace />;
    }

    return (
      <div className="min-h-screen bg-gradient-light flex flex-col relative">
        {/* Navbar */}
        <nav className="fixed top-0 left-0 right-0 bg-slate-900 text-white px-6 md:px-10 py-4 flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6 shadow-md z-50">
          {/* Logo & Brand */}
          <h1 
            onClick={() => navigate('/')}
            className="text-2xl md:text-3xl font-bold tracking-tight cursor-pointer hover:scale-105 transition-transform duration-300 flex items-center gap-3 whitespace-nowrap"
          >
            <img src="/my-mango-logo.png?v=2" alt="Mango Logo" style={{height: '45px', width: 'auto', objectFit: 'contain'}} />
            <span className="hidden sm:inline">Buyer Seller Platform</span>
            <span className="sm:hidden">BSP</span>
          </h1>

          {/* Navigation Items */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center md:justify-end w-full md:w-auto">
            <div className="flex items-center gap-2 px-3 py-2 bg-white bg-opacity-15 backdrop-blur-md rounded-full">
              <span className="text-lg">{RoleEmojis[effectiveRole]}</span>
              <span className="text-xs font-bold uppercase tracking-wider">{effectiveRole}</span>
              <span className="text-xs ml-2 hidden sm:inline opacity-75">({localStorage.getItem('userName')})</span>
            </div>
            <button 
              onClick={() => navigate('/messages')}
              className="px-3 md:px-4 py-2 bg-white bg-opacity-15 hover:bg-opacity-25 border border-white border-opacity-25 text-white rounded-lg font-bold text-xs md:text-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              💬 <span className="hidden sm:inline">Messages</span>
            </button>

            <button 
              onClick={() => navigate('/account')}
              className="px-3 md:px-4 py-2 bg-white bg-opacity-15 hover:bg-opacity-25 border border-white border-opacity-25 text-white rounded-lg font-bold text-xs md:text-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              ⚙️ <span className="hidden sm:inline">Account</span>
            </button>
            <button 
              onClick={handleLogout}
              className="px-3 md:px-4 py-2 bg-white bg-opacity-15 hover:bg-opacity-25 border border-white border-opacity-25 text-white rounded-lg font-bold text-xs md:text-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              🚪 <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </nav>

        {/* Main Content */}
        <div className="flex-1 flex flex-col pt-24 md:pt-20">
          <Routes>
            <Route path="/account" element={<AccountPage onLogout={handleLogout} userRole={effectiveRole} userId={userId} />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/messages" element={<Messaging />} />
            <Route path="/" element={
              (() => {
                switch (effectiveRole) {
                  case 'Buyer':
                    return <BuyerListings />;
                  case 'Seller':
                    return <SellerDashboard />;
                  case 'Admin':
                    return <AdminDashboard />;
                  default:
                    return <div className="text-center py-10">Unknown role: "{effectiveRole}"</div>;
                }
              })()
            } />
          </Routes>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-300 text-center py-6 px-4 border-t border-gray-700 mt-auto">
          <p className="text-xs md:text-sm font-medium">
            © 2026 Neu Edge Software Private Limited. All rights reserved.
          </p>
        </footer>
      </div>
    );
  };

  return (
    <Routes>
      <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
      <Route path="/register-seller" element={<SellerRegistrationForm />} />
      <Route path="/register-buyer" element={<BuyerRegistrationForm />} />
      <Route path="*" element={
        isLoggedIn ? (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
    </Routes>
  );
}

export default App;
